import React from 'react';
import Image from 'next/image';
import styles from './ItineraryCard.module.css';
import { AnimatedClickableDiv } from '../AnimatedClickableDiv';
import { DeleteItineraryModal } from './DeleteItineraryModal';
import { ShareIcon } from './ItineraryDetails/ItineraryDetails';
import { useTranslation } from 'react-i18next';
import { useIsSmallScreen } from '../useIsSmallScreen';
import { AlertSnackbar } from './AlertSnackbar';

interface ItineraryCardProps {
  image: string;
  title: string;
  date: string;
  itineraryId: string;
  onClick: () => void;
  onDelete?: () => void;
  showShareButton?: boolean;
  isRecommended?: boolean;
}

export const ItineraryCard: React.FC<ItineraryCardProps> = ({
  image,
  title,
  date,
  itineraryId,
  onClick,
  onDelete,
  showShareButton,
  isRecommended,
}) => {
  const { t } = useTranslation();
  const isSmallScreen = useIsSmallScreen();
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [snackbarText, setSnackbarText] = React.useState('');
  const handleShare = async () => {
    const currentUrl = new URL(window.location.href);
    const queryParams = new URLSearchParams(currentUrl.search);
    queryParams.set('pg', 'ITINERARIES.DETAILS');
    queryParams.set('itin', itineraryId);
    const itineraryUrl = `${currentUrl.origin}${currentUrl.pathname}?${queryParams.toString()}`;

    const shareData = {
      title,
      text: `Check out this itinerary: ${title}`,
      url: itineraryUrl,
    };

    try {
      if (isSmallScreen && navigator.share) {
        await navigator.share(shareData);
      } else {
        // Use copy logic for desktop or when native share is not available
        await navigator.clipboard.writeText(itineraryUrl);
        console.log('Itinerary link copied to clipboard!');
        setSnackbarText(t('Itinerary link copied to clipboard!'));
      }
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  return (
    <>
      {snackbarText && <AlertSnackbar message={snackbarText} onClose={() => setSnackbarText('')} />}
      <AnimatedClickableDiv
        className={styles.card}
        onClick={onClick}
        clickedClassName={styles.clicked}
      >
        <img src={image} className={styles.thumbnail} alt={`Thumbnail for ${title}`} />
        <div className={styles.content}>
          <div className={styles.info}>
            <div className={styles.title}>{title}</div>
            {!isRecommended && <div className={styles.date}>{date}</div>}
          </div>
          <div className={styles.buttonsContainer}>
            {showShareButton && (
              <AnimatedClickableDiv
                className={styles.shareButton}
                clickedClassName={styles.clicked}
                onClick={(e) => {
                  e.stopPropagation();
                  handleShare();
                }}
              >
                <ShareIcon />
              </AnimatedClickableDiv>
            )}
            {onDelete && (
              <AnimatedClickableDiv
                className={styles.deleteButton}
                clickedClassName={styles.clicked}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDeleteModal(true);
                }}
              >
                <Image
                  width={13}
                  height={24}
                  priority
                  src="/static/images/map/trash_outline.svg"
                  alt=""
                />
              </AnimatedClickableDiv>
            )}
          </div>
        </div>
      </AnimatedClickableDiv>
      {showDeleteModal && (
        <DeleteItineraryModal
          itineraryName={title}
          onClose={() => setShowDeleteModal(false)}
          onDelete={() => {
            onDelete?.();
            setShowDeleteModal(false);
          }}
        />
      )}
    </>
  );
};
